import useQueryData from "@/hooks/useQueryData";
import { SuccessFetchListResponse } from "@/services/api";
import { keepPreviousData, QueryKey, useQuery } from "@tanstack/react-query";
import {
  ChangeEventHandler,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ControllerRenderProps } from "react-hook-form";
import type { AsyncSelectFetchOn, Option } from "../AsyncSelect";

interface UseAsyncSelectOptions
  extends Pick<ControllerRenderProps, "value" | "onChange"> {
  queryKey: QueryKey;
  queryFn: () => Promise<SuccessFetchListResponse<Option>>;
  enabled?: boolean;
  fetchOn?: AsyncSelectFetchOn;
  onFilter?: (value: string) => void;
  defaultItem?: Partial<Option>;
  renderName?: (item: Option) => ReactNode;
}

export function useAsyncSelect({
  value,
  onChange,
  queryKey,
  queryFn,
  enabled = true,
  fetchOn = "mount",
  onFilter,
  defaultItem,
  renderName,
}: UseAsyncSelectOptions) {
  const [open, setOpen] = useState(false);
  const [filterInputValue, setFilterInputValue] = useState("");
  const shouldFetch = enabled && (fetchOn === "mount" || open);
  const query = useQuery({
    queryKey,
    queryFn,
    enabled: shouldFetch,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  });

  const { isFetching } = query;
  const data = useQueryData(query);

  const selectedId = useRef(value);

  const [selectedItem, setSelectedItem] = useState<Option | undefined>(
    defaultItem?.name ? (defaultItem as Option) : undefined,
  );

  useEffect(() => {
    selectedId.current = value;

    if (!value) {
      setSelectedItem(undefined);
      return;
    }

    const itemFromQuery = data?.find((item) => item.id === value);
    if (itemFromQuery) {
      setSelectedItem(itemFromQuery);
      return;
    }

    if (defaultItem?.id) {
      setSelectedItem(defaultItem as Option);
    }
  }, [value, defaultItem, data]);

  const onValueChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const next = event.target.value;
      setFilterInputValue(next);
      onFilter?.(next);
    },
    [onFilter],
  );

  const onSelect = useCallback(
    (id: string) => {
      const found = data?.find((item) => item.id === id);
      setSelectedItem(found);
      selectedId.current = id;
      if (found) {
        onChange(id, found);
      } else {
        onChange(undefined);
      }
      setFilterInputValue("");
      setOpen(false);
    },
    [onChange, data],
  );

  const onClearSelection = useCallback(() => {
    onChange("");
    setFilterInputValue("");
    setOpen(false);
  }, [onChange]);

  const onOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (isOpen) {
        onFilter?.("");
        setFilterInputValue("");
      }
    },
    [onFilter],
  );

  const onClear = useCallback(() => {
    onFilter?.("");
    setFilterInputValue("");
  }, [onFilter]);

  const buttonLabel = useMemo(() => {
    if (renderName && selectedItem) {
      return renderName(selectedItem);
    }
    return selectedItem?.name;
  }, [renderName, selectedItem]);

  return {
    open,
    onOpenChange,
    query,
    data,
    isFetching,
    filterInputValue,
    onValueChange,
    onClear,
    onSelect,
    onClearSelection,
    selectedId,
    buttonLabel,
  };
}
